import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { AmigosChat, AmigosPicker, AmigosTopics } from "@/components/AmigosChat";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Flame, Loader2, Lock, Plus, Sparkles, Trash2, Volume2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  superadmin: "Admin",
  academic_director: "Dirección académica",
  teacher: "Profesores",
  student: "Estudiantes",
};

/** Student-facing practice page: same friends, saved progress, higher daily limit. */
export default function AmigosPractice() {
  const [characters, setCharacters] = useState([]);
  const [picked, setPicked] = useState(null);
  const [topicId, setTopicId] = useState(null);
  const [newTopic, setNewTopic] = useState({ title: "", prompt: "", kind: "chat" });
  const [customTopics, setCustomTopics] = useState(null);
  const [progress, setProgress] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [voiceData, setVoiceData] = useState(null);   // { voices, current, characters }

  const loadProgress = () => api.get("/amigos/progress").then((r) => setProgress(r.data)).catch(() => setProgress(null));
  useEffect(() => {
    api.get("/amigos/settings").then((r) => {
      setCfg(r.data);
      if (r.data.can_manage) {
        api.get("/amigos/voices").then((v) => setVoiceData(v.data)).catch(() => {});
        api.get("/amigos/topics", { params: { character_id: "tavo" } })
          .then((t) => setCustomTopics((t.data.topics || []).filter((x) => x.custom))).catch(() => {});
      }
      if (r.data.allowed) {
        api.get("/amigos/characters").then((c) => setCharacters(c.data || []));
        loadProgress();
      }
    });
  }, []);

  const saveCfg = async (patch) => {
    const next = { roles: cfg.roles, public_enabled: cfg.public_enabled, ...patch };
    try {
      const r = await api.put("/amigos/settings", next);
      setCfg({ ...cfg, ...r.data });
      toast.success("Guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const pickVoice = async (charId, voiceId) => {
    try {
      const r = await api.put("/amigos/voices", { voices: { [charId]: voiceId } });
      setVoiceData((v) => ({ ...v, current: r.data.voices }));
      const p = await api.post("/amigos/voice-preview", { character_id: charId, voice_id: voiceId });
      if (p.data.audio_url) new Audio(`${API_BASE}${p.data.audio_url}`).play().catch(() => {});
      toast.success("Voz guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar la voz");
    }
  };

  const hearVoice = async (charId) => {
    try {
      const p = await api.post("/amigos/voice-preview", { character_id: charId });
      if (p.data.audio_url) new Audio(`${API_BASE}${p.data.audio_url}`).play().catch(() => {});
    } catch {
      toast.error("No pudimos generar la prueba");
    }
  };

  const addTopic = async () => {
    if (!newTopic.title.trim() || !newTopic.prompt.trim()) {
      toast.error("Poné un título y de qué se trata");
      return;
    }
    try {
      const r = await api.post("/amigos/topics", newTopic);
      setCustomTopics((r.data.topics || []).map((t) => ({ ...t, custom: true })));
      setNewTopic({ title: "", prompt: "", kind: "chat" });
      toast.success("Tema agregado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar");
    }
  };

  const removeTopic = async (id) => {
    const r = await api.delete(`/amigos/topics/${id}`);
    setCustomTopics((r.data.topics || []).map((t) => ({ ...t, custom: true })));
  };

  const forgetMe = async () => {
    await api.delete("/amigos/memory");
    loadProgress();
    toast.success("Tu amigo ya no recuerda nada de vos");
  };

  const toggleRole = (role, on) => {
    const roles = on ? [...new Set([...cfg.roles, role])] : cfg.roles.filter((r) => r !== role);
    saveCfg({ roles });
  };

  if (!cfg) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="amigos-practice-page">
      <PageHeader title="Practicá hablando" subtitle="Conversá en voz alta con tus amigos ticos cuando querás" />

      {cfg.can_manage && (
        <Card className="p-5 border-brand-blue/30 bg-brand-blue/5" data-testid="amigos-settings-card">
          <h3 className="text-sm font-semibold text-slate-900">¿Quién ve esta actividad?</h3>
          <p className="text-[11px] text-slate-600 mt-1 mb-3">
            Apagá los perfiles que todavía no deben verla. Admin siempre la ve para poder probarla.
          </p>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {(cfg.all_roles || []).map((role) => (
              <label key={role} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700">{ROLE_LABELS[role] || role}</span>
                <Switch
                  data-testid={`amigos-role-${role}`}
                  disabled={role === "superadmin"}
                  checked={role === "superadmin" || cfg.roles.includes(role)}
                  onCheckedChange={(v) => toggleRole(role, v)}
                />
              </label>
            ))}
            <label className="flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2 sm:col-span-2">
              <span className="text-sm text-slate-700">
                Página pública <span className="text-slate-400">(/amigos, visitantes sin cuenta)</span>
              </span>
              <Switch
                data-testid="amigos-public-switch"
                checked={!!cfg.public_enabled}
                onCheckedChange={(v) => saveCfg({ public_enabled: v })}
              />
            </label>
          </div>
        </Card>
      )}

      {cfg.can_manage && voiceData && (
        <Card className="p-5" data-testid="amigos-voices-card">
          <h3 className="text-sm font-semibold text-slate-900">La voz de cada amigo</h3>
          <p className="text-[11px] text-slate-600 mt-1 mb-3">
            Voces latinas de ElevenLabs. Al elegir una la escuchás de una; si se acaba el saldo, el
            amigo sigue hablando con la voz de respaldo.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(voiceData.characters || []).map((c) => (
              <div key={c.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-medium text-slate-800">{c.name}</span>
                  <Button variant="ghost" size="sm" data-testid={`amigos-voice-play-${c.id}`}
                          onClick={() => hearVoice(c.id)}>
                    <Volume2 className="h-4 w-4" /> Escuchar
                  </Button>
                </div>
                <Select value={voiceData.current?.[c.id] || ""} onValueChange={(v) => pickVoice(c.id, v)}>
                  <SelectTrigger data-testid={`amigos-voice-select-${c.id}`}>
                    <SelectValue placeholder="Elegí una voz" />
                  </SelectTrigger>
                  <SelectContent>
                    {(voiceData.voices || []).map((v) => (
                      <SelectItem key={v.voice_id} value={v.voice_id}>
                        {v.name} · {v.accent} · {v.gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!cfg.allowed && (
        <Card className="p-8 text-center" data-testid="amigos-disabled-notice">
          <Lock className="h-6 w-6 text-slate-400 mx-auto" />
          <p className="text-sm text-slate-600 mt-3">
            Esta actividad todavía no está habilitada para tu perfil.
          </p>
        </Card>
      )}

      {cfg.allowed && progress && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card className="p-4 flex items-center gap-3" data-testid="amigos-stat-stars">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <div><p className="text-2xl font-bold text-slate-900">{progress.stars}</p>
              <p className="text-xs text-slate-500">estrellas ganadas</p></div>
          </Card>
          <Card className="p-4 flex items-center gap-3" data-testid="amigos-stat-streak">
            <Flame className="h-5 w-5 text-brand-orange" />
            <div><p className="text-2xl font-bold text-slate-900">{progress.streak}</p>
              <p className="text-xs text-slate-500">días seguidos practicando</p></div>
          </Card>
          <Card className="p-4 flex items-center gap-3" data-testid="amigos-stat-words">
            <span className="h-5 w-5 grid place-items-center text-emerald-600 font-bold">ES</span>
            <div><p className="text-2xl font-bold text-slate-900">{(progress.words || []).length}</p>
              <p className="text-xs text-slate-500">palabras nuevas guardadas</p></div>
          </Card>
        </div>
      )}

      {!cfg.allowed ? null : picked && topicId ? (
        <AmigosChat character={picked} topicId={topicId}
                    onBack={() => { setTopicId(null); setPicked(null); loadProgress(); }} />
      ) : picked ? (
        <AmigosTopics character={picked} onStart={setTopicId} onBack={() => setPicked(null)} />
      ) : characters.length === 0 ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>
      ) : (
        <AmigosPicker characters={characters} onPick={setPicked} />
      )}

      {cfg.allowed && !picked && progress?.facts?.length > 0 && (
        <Card className="p-5" data-testid="amigos-memory-card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Lo que tu amigo recuerda de vos</h3>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={forgetMe}
                    data-testid="amigos-forget-btn">
              <Trash2 className="h-4 w-4" /> Olvidar todo
            </Button>
          </div>
          <ul className="space-y-1 text-sm text-slate-600 list-disc pl-5">
            {progress.facts.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </Card>
      )}

      {cfg.can_manage && !picked && customTopics && (
        <Card className="p-5" data-testid="amigos-topics-admin">
          <h3 className="text-sm font-semibold text-slate-900">Mis temas propios</h3>
          <p className="text-[11px] text-slate-600 mt-1 mb-3">
            Se suman al catálogo fijo. Escribí de qué se trata como si le hablaras al amigo
            (ej. "Hablen de la Semana Santa y las tradiciones ticas").
          </p>
          {customTopics.length > 0 && (
            <ul className="space-y-2 mb-4">
              {customTopics.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
                  <span className="text-sm text-slate-700">{t.title}</span>
                  <Button variant="ghost" size="sm" className="text-red-600"
                          onClick={() => removeTopic(t.id)} data-testid={`amigos-topic-del-${t.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
            <Input placeholder="Título (ej. Semana Santa)" value={newTopic.title}
                   data-testid="amigos-new-topic-title"
                   onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })} />
            <Input placeholder="De qué se trata la conversación" value={newTopic.prompt}
                   data-testid="amigos-new-topic-prompt"
                   onChange={(e) => setNewTopic({ ...newTopic, prompt: e.target.value })} />
            <Button onClick={addTopic} data-testid="amigos-new-topic-add">
              <Plus className="h-4 w-4" /> Agregar
            </Button>
          </div>
        </Card>
      )}

      {cfg.allowed && !picked && progress?.words?.length > 0 && (
        <Card className="p-5" data-testid="amigos-word-bank">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Mis palabras nuevas</h3>
          <div className="flex flex-wrap gap-1.5">
            {progress.words.map((w, i) => (
              <span key={`${w.es}${i}`} className="text-xs rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1">
                {w.es}{w.en ? ` · ${w.en}` : ""}
              </span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
